import {
  AntiSaccadesCustomConfiguration,
  AssistanceVideo,
  AvailablePreTestConfigurationOptions,
  AvailableReadingAssessmentSpecificConfigurationOptions,
  CPTConfigMode,
  CPTCustomConfiguration,
  ColorVisionCustomConfiguration,
  ConfigurationChoice,
  EyeType,
  KineticPerimetryPattern,
  KineticTestCustomConfiguration,
  OMTestCustomConfiguration,
  SmoothPursuitCustomConfiguration,
  PersistedConfiguration,
  ReadingAssessmentCatalogEntry,
  ReadingAssessmentCustomConfiguration,
  TestCategory,
  TestConfiguration,
  TestProgressConfiguration,
  TestSpecificConfig,
  TestStatus,
  TestType,
  VirtualAssistantConfig,
  VisualAcuityCustomConfiguration,
} from "types/testTypes";
import {
  BATCH_UPDATE,
  RESET_PRODUCT_STATE,
  UPDATE_CURRENT_VERSION,
  UPDATE_PRODUCT_SCOPE,
  UPDATE_SCENE,
} from "@utils/redux-utils/constants";
import {
  CPTAdultCustomDefaultConfig,
  CPTAdultCustomDemoConfig,
  CPTCustomDefaultConfig,
  CPTCustomDemoConfig,
} from "@constants/testConstants/testSpecificConstants/cptCustomCalibrations";
import {
  GetUpdatedAvailableIshiharaConfigsAfterConfigUpdate,
  translateColorVisionPlates,
} from "@utils/test-specific-utils/ColorVisionTestUtils";
import {
  GetUpdatedAvailableKineticConfigsAfterConfigUpdate,
  getKineticPatternDefaults,
} from "@utils/test-specific-utils/KineticPerimetryUtils";
import {
  TestSuiteItem,
  loadTestSuite,
  moveToNextTest,
  reorderTests,
  restartTestInSuite,
  setAutoPlayCountdown,
  setSelectedTest,
  startNextTest,
  updateTestStatus,
} from "@store/testSuite/testSuiteSlice";
import {
  addCompletedTestId,
  incrementVfaAutoRestartCount,
  resetTestSession,
  resetVfaAutoRestartCount,
} from "@store/testsession/testSessionSlice";
import {
  selectVfaAutoRestartCount,
  selectVfaAutoRestartTestCategory,
} from "@store/testsession/testSessionSelectors";
import {
  CalibrationLifecycleStatus,
  addVideosToDoNotShowAgainList,
  applyAvailableConfigurations,
  clearDoNotShowAgainList,
  clearTestConfigurations,
  clearTestRelatedData,
  clearCollectedTestData,
  loadSavedTestConfiguration,
  manualCheckTestCompletion,
  mergeProgressConfigToMain,
  resetPauseState,
  resetSavedTestConfiguration,
  resumeTest,
  saveTestConfiguration,
  selectTest,
  sendMessageToHeadSet,
  sendTestStartMessage,
  sendTestTerminateMessage,
  attachToRunningTest,
  setAvailableConfigurations,
  setAvailableProgressConfigurations,
  setBaseAvailableConfigurations,
  setCalibrationLifecycle,
  setCalibrationUnresponsive,
  setCurrentVirtualAssistanceVideo,
  setIsCurrentlyCalibrating,
  setIsLastBackToBackEyeTest,
  setIsSceneReady,
  setManualCompletionCheckFailed,
  setManualCompletionCheckInProgress,
  setManualCompletionCheckSucceeded,
  setReadingAssessmentCatalog,
  setReadingAssessmentCatalogError,
  setShouldWaitForNextEyeOnFinish,
  setTestCompletedPercentage,
  setTestConfigurationExtraData,
  setTestEventCodes,
  setTestId,
  setTestPhase,
  setTestProgressConfiguration,
  setTestStatus,
  setTestUIConfiguration,
  setVirtualAssistantConfigVideoList,
  startTestingNextEyeInBackToBackTest,
  testConfigurationUpdateComplete,
  triggerVfaReliabilityAutoRestart,
  updatePersistedConfigFromTestConfig,
  updateSavedTestConfiguration,
  updateTestConfiguration,
} from "@store/tests/testSlice";
import { ascendingInstruction, thresholdInstruction } from "@constants/instructionConstants";
import { call, cancel, delay, fork, put, race, select, take, takeLatest } from "redux-saga/effects";
import {
  clearAwaitingMenuScene,
  clearTestProgress,
  setLatestTestInteractionDate,
  updateDeviceStatus,
  updateOnTestProgressPage,
  updateScene,
} from "@actions/productActions";
import {
  DEVICE_STATUS,
  isCalibrationScene,
} from "@components/new_test_ui/new_test_steps/common/DeviceStatus";
import { navigate } from "@utils/navigationService";
import { cloneDeep, isEqual, merge } from "lodash";
import { closeConfirmation, openConfirmation } from "@actions/actions";
import {
  applyFirstLanguageToVirtualAssistant,
  extractSavedFields,
  hasLoadingMandatoryConfigurationOption,
  mergePersistedFields,
} from "@utils/configurationUtils";
import {
  filterTestConfigurationOptions,
  isVersionEnoughForBackToBackEyeTest,
  sanitizeTestConfiguration,
} from "@utils/VersionUtils";
import {
  getSelectedTestName,
  selectAvailablePreTestConfigurationOptions,
  selectAvailableProgressConfigurationOptions,
  selectBaseAvailablePreTestConfigurationOptions,
  selectCalibrationLifecycle,
  selectIsCurrentlyCalibrating,
  selectIsDeviceCalibrationActive,
  selectIsOperatorPaused,
  selectIsResumedTestSession,
  selectPersistedConfiguration,
  selectReadingAssessmentCatalog,
  selectSavedTestConfiguration,
  selectShouldWaitForNextEyeOnFinish,
  selectTestCompletionCheckData,
  selectTestConfiguration,
  selectTestEventCodes,
  selectTestId,
  selectTestProgressConfiguration,
  selectTestStatus,
  selectTestUIConfiguration,
  selectVirtualAssistantConfig,
  selectVirtualAssistantDoNotShowAgainList,
} from "./testSelectors";
import {
  omConfigAntiSaccades,
  omConfigCircularPursuit,
  omConfigXIH,
} from "@constants/testConstants/testSpecificConstants/ocularMotilityConstants";
import {
  pupillometryCustomConfig,
  rapdCustomConfig,
} from "@constants/testConstants/testSpecificConstants/pupillometryConstants";
import {
  selectActiveTest,
  selectActiveTestIndex,
  selectAutoPlay,
  selectIsSuiteRunning,
  selectSelectedTest,
  selectTestSuiteTests,
} from "@store/testSuite/testSuiteSelectors";
import { isTestStatusRunning } from "@components/test-suite/constants";
import {
  selectMovingFixationCatchTrialsEnabled,
  selectOcularMotilityAdminConfiguration,
} from "@store/adminConfigurations/adminConfigurationsSelectors";
import { OcularMotilityAdminConfiguration } from "types/adminConfigurationTypes";
import {
  selectAwaitingMenuSince,
  selectCurrentVersion,
  selectIsAwaitingMenuSceneAfterCompletion,
  selectIsReliabilityAutoRestartAllowed,
  selectProductId,
  selectScene,
  selectScopeList,
} from "@utils/redux-utils/selectors/productSelectors";
import {
  reconnectAndRequestCurrentTestDetails,
  sendGenericNextMessage,
  sendProgressConfigurationUpdateMessage,
  sendSkipCalibration,
  sendTestFinishRequest,
  sendTestStartRequest,
  sendTestTerminateRequest,
} from "@utils/api-communication/WsUtil";
import {
  decideStartAgainstRunningTest,
  recordTestStartOnThisPage,
  runningTestIdOnHeadset,
  sceneOfRunningTest,
  testStatusForRunningScene,
} from "./runningTestGuard";

import { GetUpdatedAvailableOMConfigsAfterConfigUpdate } from "@utils/test-specific-utils/OcularMotilityUtils";
import { GetUpdatedAvailableVFAConfigsAfterConfigUpdate } from "@utils/test-specific-utils/PerimetryTestUtils";
import { MovingFixationCatchTrialAvailability } from "@utils/VFA/movingFixationCatchTrials";
import { VFA_RELIABILITY_MAX_AUTO_RESTARTS } from "@utils/test-specific-utils/vfaReliabilityUtils";
import { GetUpdatedAvailableVisualAcuityConfigsAfterConfigUpdate } from "@utils/test-specific-utils/VisualAcuityUtils";
import { HeadsetVersion } from "types/productTypes";
import { LANGUAGE_DATA_MAP } from "@constants/languageConstants";
import { PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import type { Task } from "redux-saga";
import { SecureTokenManager } from "@utils/auth/secureTokenManager";
import { TEST_DEFINITIONS } from "@constants/testConstants/testDefinitions";
import { TestCompletionService } from "@utils/api-communication/TestCompletionService";
import color_vision_plates_en from "@data/color_vision_plates.json";
import {
  DeviceRunState,
  getDeviceRunState,
  getReadingLayoutCatalog,
} from "@utils/api-communication/RestApiCli";
import { getVirtualAssistantVideoListForTest } from "@constants/virtualAssistanceConstants";
import i18n from "i18next";
import { logOut } from "@actions/userActions";
import { resolveInScopeTest } from "@utils/UIUtils";
import { selectSelectedPatient } from "@store/patients/patientSelectors";
import { selectUserOrgId } from "@utils/redux-utils/selectors/userSelectors";
import { store } from "@store/index";
import { toggleDoTheOtherEyeButtonState } from "@store/ui/uiSlice";
import { v4 as uuidv4 } from "uuid";
import { waitForActions } from "@utils/sagaUtils";
import {
  evaluateOfflineHeadsetUpdatePolicy,
  formatOfflineHeadsetUpdateDeadline,
  hasOfflineHeadsetUpdateWarningBeenShown,
  markOfflineHeadsetUpdateWarningAsShown,
  OFFLINE_HEADSET_UPDATE_POLICY,
  openOfflineHeadsetUpdatePage,
} from "@utils/offlineHeadsetUpdatePolicy";
import {
  licenceReadOnlyMessageKey,
  readLicenceStatus,
  LICENCE_READ_ONLY_TITLE_KEY,
} from "@store/licence/licenceSagas";
import { setLicenceStatus, type LicenceState } from "@store/licence/licenceSlice";

const RECOVERY_DIALOG_POLL_INTERVAL_MS = 4000;
const RECOVERY_DIALOG_POLL_MAX_ATTEMPTS = 15;
export const START_MENU_WAIT_TIMEOUT_MS = 8000;

function* resetActiveSuiteTestToNotStarted(): Generator<any, void, any> {
  const activeTestIndex: number | null = yield select(selectActiveTestIndex);
  const suiteTests: TestSuiteItem[] = yield select(selectTestSuiteTests);
  const activeSuiteTest = activeTestIndex === null ? null : suiteTests[activeTestIndex];
  if (activeSuiteTest) {
    yield put(updateTestStatus({ id: activeSuiteTest.id, status: "not started", progress: 0 }));
  }
}

function* abortStartAndReturnToConfiguration(): Generator<any, void, any> {
  yield call(resetActiveSuiteTestToNotStarted);
  yield put(setTestStatus({ status: TestStatus.TestConfiguration }));
}

function* enforceLicenceReadOnlyPolicy(): Generator<any, boolean, any> {
  const licence: LicenceState = yield call(readLicenceStatus);
  yield put(setLicenceStatus(licence));

  if (!licence.readOnly) {
    return false;
  }

  yield call(abortStartAndReturnToConfiguration);
  yield put(
    openConfirmation(
      i18n.t(LICENCE_READ_ONLY_TITLE_KEY),
      i18n.t(licenceReadOnlyMessageKey(licence.state)),
      () => {},
      false,
      "close",
    ),
  );
  return true;
}

function* enforceOfflineHeadsetUpdatePolicy(
  action: PayloadAction<{ isRetry: boolean }>,
): Generator<any, boolean, any> {
  const currentVersion: HeadsetVersion = yield select(selectCurrentVersion);
  const policyStatus = evaluateOfflineHeadsetUpdatePolicy({ version: currentVersion });

  if (policyStatus === "allowed") {
    return false;
  }

  const interpolationValues = {
    currentVersion: currentVersion || i18n.t("OfflineHeadsetUpdate.unknownVersion"),
    minimumVersion: OFFLINE_HEADSET_UPDATE_POLICY.minimumVersion,
    deadline: formatOfflineHeadsetUpdateDeadline(i18n.language),
  };

  if (policyStatus === "warning") {
    if (hasOfflineHeadsetUpdateWarningBeenShown()) {
      return false;
    }

    yield put(
      openConfirmation(
        i18n.t("OfflineHeadsetUpdate.title"),
        i18n.t("OfflineHeadsetUpdate.warningMessage", interpolationValues),
        () => {
          markOfflineHeadsetUpdateWarningAsShown();
          openOfflineHeadsetUpdatePage();
        },
        false,
        "OfflineHeadsetUpdate.continueTest",
        "OfflineHeadsetUpdate.openUpdatePage",
        () => {
          markOfflineHeadsetUpdateWarningAsShown();
          store.dispatch(sendTestStartMessage(action.payload));
        },
      ),
    );
    return true;
  }

  yield call(abortStartAndReturnToConfiguration);
  yield put(
    openConfirmation(
      i18n.t("OfflineHeadsetUpdate.title"),
      i18n.t("OfflineHeadsetUpdate.blockedMessage", interpolationValues),
      openOfflineHeadsetUpdatePage,
      false,
      "close",
      "OfflineHeadsetUpdate.openUpdatePage",
    ),
  );
  return true;
}

function withReadingAssessmentCatalogChoices(
  availableOptions: AvailablePreTestConfigurationOptions,
  catalog: ReadingAssessmentCatalogEntry[],
) {
  const nextOptions = cloneDeep(availableOptions);
  const readingAssessmentOptions = nextOptions.testSpecificConfigurationOptions as
    | AvailableReadingAssessmentSpecificConfigurationOptions
    | undefined;

  if (!readingAssessmentOptions?.textListType) {
    return nextOptions;
  }

  const textListType = readingAssessmentOptions.textListType;
  textListType.choices = (Array.isArray(catalog) ? catalog : []).map(
    (entry) =>
      ({
        value: entry.textId,
        label: entry.title?.trim() || entry.textId,
        validSinceVersion: textListType.validSinceVersion ?? undefined,
      }) as ConfigurationChoice<string>,
  );
  textListType.isLoading = false;

  return nextOptions;
}

function withReadingAssessmentCatalogLoadingState(
  availableOptions: AvailablePreTestConfigurationOptions,
) {
  const nextOptions = cloneDeep(availableOptions);
  const readingAssessmentOptions = nextOptions.testSpecificConfigurationOptions as
    | AvailableReadingAssessmentSpecificConfigurationOptions
    | undefined;

  if (!readingAssessmentOptions?.textListType) {
    return nextOptions;
  }

  readingAssessmentOptions.textListType = {
    ...readingAssessmentOptions.textListType,
    choices: [],
    isLoading: true,
  };

  return nextOptions;
}

function* resolveReadingAssessmentAvailableOptions(
  rawOptions: AvailablePreTestConfigurationOptions,
) {
  const testName: TestCategory = yield select(getSelectedTestName);
  if (testName !== TestCategory.ReadingAssessment) {
    return rawOptions;
  }

  const cachedCatalog: ReadingAssessmentCatalogEntry[] = yield select(
    selectReadingAssessmentCatalog,
  );
  if (cachedCatalog.length > 0) {
    return withReadingAssessmentCatalogChoices(rawOptions, cachedCatalog);
  }

  const productVersion = yield select(selectCurrentVersion);
  const catalogScopeList = yield select(selectScopeList);
  yield put(
    applyAvailableConfigurations({
      availableOptions: filterTestConfigurationOptions(
        withReadingAssessmentCatalogLoadingState(rawOptions),
        productVersion,
        catalogScopeList ?? [],
      ),
      productVersion,
    }),
  );
  try {
    const catalogResponse: ReadingAssessmentCatalogEntry[] | null | undefined =
      yield call(getReadingLayoutCatalog);
    const catalog = Array.isArray(catalogResponse) ? catalogResponse : [];
    yield put(setReadingAssessmentCatalog(catalog));
    yield put(setReadingAssessmentCatalogError(null));
    return withReadingAssessmentCatalogChoices(rawOptions, catalog);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Failed to load reading layouts";
    yield put(setReadingAssessmentCatalogError(message));
    return withReadingAssessmentCatalogChoices(rawOptions, []);
  }
}

function* handleTestSelectionChange(action: PayloadAction<TestCategory>) {
  const testDefinition = TEST_DEFINITIONS[action.payload];
  if (!testDefinition) {
    throw new Error(`Invalid Test Name or Missing Definition: ${action.payload}`);
  }

  const vfaAutoRestartTestCategory: TestCategory | null = yield select(
    selectVfaAutoRestartTestCategory,
  );

  const productVersion = yield select(selectCurrentVersion);
  yield put(clearTestConfigurations(action.payload));
  yield put(setTestUIConfiguration(testDefinition.uiConfiguration));
  yield put(
    setAvailableConfigurations({
      availableOptions: testDefinition.availableOptions,
      productVersion,
      isNewTestSelection: true,
    }),
  );
  yield put(setTestEventCodes(testDefinition.eventCodes));

  if (vfaAutoRestartTestCategory !== action.payload) {
    yield put(resetVfaAutoRestartCount());
  }
}

function* fetchAndUpdateVirtualAssistanceVideos() {
  const currentTestConfiguration = yield select(selectTestConfiguration);
  const doNotShowAgainList = yield select(selectVirtualAssistantDoNotShowAgainList);
  const productVersion = yield select(selectCurrentVersion);
  if (!currentTestConfiguration) {
    return;
  }
  const videoList = yield call(
    getVirtualAssistantVideoListForTest,
    currentTestConfiguration,
    doNotShowAgainList,
    productVersion,
  );
  yield put(setVirtualAssistantConfigVideoList(videoList));
}

// Smooth pursuit is an orbit on the headset (SmoothPursuitStageConfig), while XIH
// and the legacy circular pursuit are patterns (OcularMotilityTestStageConfig).
// Sending one shape where the other is expected deserializes to the headset's own
// defaults without complaint, so each test type gets its own fields.
function resolveOcularMotilityExtraData(
  testType: TestType | undefined,
  adminConfiguration: OcularMotilityAdminConfiguration,
  preservedChildMode: boolean | undefined,
): OMTestCustomConfiguration | SmoothPursuitCustomConfiguration | null {
  if (testType === TestType.XIH) {
    return { ...omConfigXIH, ...adminConfiguration.xih };
  }
  if (testType === TestType.SmoothPursuit) {
    return preservedChildMode === undefined
      ? { ...adminConfiguration.smoothPursuit }
      : { ...adminConfiguration.smoothPursuit, childMode: preservedChildMode };
  }
  if (testType === TestType.CircularPursuit) {
    return preservedChildMode === undefined
      ? omConfigCircularPursuit
      : { ...omConfigCircularPursuit, childMode: preservedChildMode };
  }
  return null;
}

function* handleTestConfigurationUpdate(action: PayloadAction<Partial<TestConfiguration>>) {
  const selectedTestName = yield select(getSelectedTestName);
  const currentConfiguration: TestConfiguration = yield select(selectTestConfiguration);

  const selectedTestType = currentConfiguration?.testType ?? action.payload?.testType;
  const extraData: TestSpecificConfig | null = currentConfiguration?.extraData;
  const version = yield select(selectCurrentVersion);
  const testStatus = yield select(selectTestStatus);
  const eyeConfiguration =
    action.payload?.eyeConfiguration ?? currentConfiguration?.eyeConfiguration;
  const rawEyeType =
    action.payload?.eyeConfiguration?.eyeType ?? currentConfiguration?.eyeConfiguration?.eyeType;
  const isBackToBackAllowed = isVersionEnoughForBackToBackEyeTest(version);
  const wasRightThenLeftCoerced = rawEyeType === EyeType.RightThenLeft && !isBackToBackAllowed;
  const eyeType = wasRightThenLeftCoerced ? EyeType.RightEye : rawEyeType;
  const isBackToBackEyeTest = eyeType === EyeType.RightThenLeft;
  const currentIsBackToBackEyeTest = currentConfiguration?.eyeConfiguration?.isBackToBackEyeTest;
  const uiConfiguration = yield select(selectTestUIConfiguration);
  if (isBackToBackEyeTest || eyeType === EyeType.BothEye) {
    yield put(
      setTestUIConfiguration({
        ...uiConfiguration,
        isTestOtherEyeOptionPermitted: false,
      }),
    );
  } else {
    yield put(
      setTestUIConfiguration({
        ...uiConfiguration,
        isTestOtherEyeOptionPermitted: true,
      }),
    );
  }

  yield put(setShouldWaitForNextEyeOnFinish(isBackToBackEyeTest));

  if (isBackToBackEyeTest !== currentIsBackToBackEyeTest || wasRightThenLeftCoerced) {
    yield put(
      updateTestConfiguration({
        eyeConfiguration: { ...eyeConfiguration, eyeType, isBackToBackEyeTest },
      } as Partial<TestConfiguration>),
    );
  }

  yield put(setTestProgressConfiguration(currentConfiguration as TestConfiguration));

  switch (selectedTestName) {
    case TestCategory.VisualField: {
      if (
        testStatus === TestStatus.VirtualAssistant ||
        testStatus === TestStatus.Started ||
        testStatus === TestStatus.StartRequested
      )
        return;

      const currentConfigOptions = yield select(selectAvailablePreTestConfigurationOptions);
      const baseConfigOptions = yield select(selectBaseAvailablePreTestConfigurationOptions);
      const catchTrialsSwitchedOn: boolean = yield select(selectMovingFixationCatchTrialsEnabled);
      const changedConfigOptions = GetUpdatedAvailableVFAConfigsAfterConfigUpdate(
        currentConfiguration as TestConfiguration,
        baseConfigOptions,
        { catchTrialsSwitchedOn, headsetVersion: version },
      );

      if (!isEqual(changedConfigOptions, currentConfigOptions)) {
        yield put(
          setAvailableConfigurations({
            availableOptions: changedConfigOptions,
            productVersion: version,
          }),
        );
      }

      break;
    }
    case TestCategory.VisualAcuity: {
      const currentConfigOptions = yield select(selectAvailablePreTestConfigurationOptions);
      const baseConfigOptions = yield select(selectBaseAvailablePreTestConfigurationOptions);
      const changedConfigOptions = GetUpdatedAvailableVisualAcuityConfigsAfterConfigUpdate(
        currentConfiguration as TestConfiguration,
        baseConfigOptions,
      );

      if (!isEqual(changedConfigOptions, currentConfigOptions)) {
        yield put(
          setAvailableConfigurations({
            availableOptions: changedConfigOptions,
            productVersion: version,
          }),
        );
      }

      const currentVaExtraData = (currentConfiguration?.extraData ??
        {}) as VisualAcuityCustomConfiguration;
      if (currentConfiguration?.testType === TestType.Logmar && currentVaExtraData.showTutorial) {
        yield put(
          setTestConfigurationExtraData({
            extraData: { ...currentVaExtraData, showTutorial: false },
          }),
        );
      }

      break;
    }
    case TestCategory.FDT:
    case TestCategory.ContrastSensitivity:
    case TestCategory.AmslerGrid:
    case TestCategory.Cover:
    case TestCategory.StereoAcuity:
    case TestCategory.Videonystagmography:
    case TestCategory.Dyscalculia:
    case TestCategory.WisconsinCardSorting:
    case TestCategory.BinocularVision:
    case TestCategory.OptokineticNystagmus:
      break;
    case TestCategory.Kinetic: {
      const kineticExtraData =
        (currentConfiguration?.extraData as KineticTestCustomConfiguration) ?? null;
      const testPattern = kineticExtraData?.testPattern;

      // Set default values when pattern changes to FF_CUSTOM
      if (testPattern === KineticPerimetryPattern.FF_CUSTOM) {
        const defaults = getKineticPatternDefaults(
          testPattern,
          kineticExtraData?.testTypeCircleAngle,
          kineticExtraData?.numberOfStimuliStarts,
        );

        if (
          kineticExtraData?.testTypeCircleAngle == null ||
          kineticExtraData?.numberOfStimuliStarts == null
        ) {
          yield put(
            setTestConfigurationExtraData({
              extraData: {
                ...kineticExtraData,
                testTypeCircleAngle:
                  kineticExtraData?.testTypeCircleAngle ?? defaults.testTypeCircleAngle,
                numberOfStimuliStarts:
                  kineticExtraData?.numberOfStimuliStarts ?? defaults.numberOfStimuliStarts,
              } as KineticTestCustomConfiguration,
            }),
          );
        }
      }

      const currentConfigOptions = yield select(selectAvailablePreTestConfigurationOptions);
      const baseConfigOptions = yield select(selectBaseAvailablePreTestConfigurationOptions);
      const changedConfigOptions = GetUpdatedAvailableKineticConfigsAfterConfigUpdate(
        currentConfiguration as TestConfiguration,
        baseConfigOptions,
      );

      if (!isEqual(changedConfigOptions, currentConfigOptions)) {
        yield put(
          setAvailableConfigurations({
            availableOptions: changedConfigOptions,
            productVersion: version,
          }),
        );
      }

      break;
    }
    case TestCategory.ReadingAssessment: {
      const textListType = (extraData as ReadingAssessmentCustomConfiguration)?.textListType;
      if (textListType) {
        let extraData: ReadingAssessmentCustomConfiguration =
          currentConfiguration?.extraData as ReadingAssessmentCustomConfiguration;
        extraData = {
          ...extraData,
          testIds: [textListType],
        } as ReadingAssessmentCustomConfiguration;
        yield put(
          setTestConfigurationExtraData({
            extraData,
          }),
        );
      }
      break;
    }
    case TestCategory.Pupillometry:
      {
        if (selectedTestType === TestType.RAPD) {
          yield put(setTestConfigurationExtraData({ extraData: rapdCustomConfig }));
        } else if (selectedTestType === TestType.Pupillometry) {
          yield put(setTestConfigurationExtraData({ extraData: pupillometryCustomConfig }));
        }
      }
      break;
    case TestCategory.Dyslexia: {
      const testLanguage = currentConfiguration?.testLanguage;
      if (testLanguage) {
        if (!LANGUAGE_DATA_MAP[testLanguage]?.orientation) {
          console.warn(`Language data not found for language: ${testLanguage}`);
          return;
        }
        yield put(
          setTestConfigurationExtraData({
            extraData: {
              textOrientation: LANGUAGE_DATA_MAP[testLanguage].orientation,
            },
          }),
        );
      }
      break;
    }
    case TestCategory.ContinuousPerformance:
      const cptConfigMode = (extraData as CPTCustomConfiguration)?.cptConfigMode;
      if (cptConfigMode) {
        let extraData: CPTCustomConfiguration;

        switch (cptConfigMode) {
          case CPTConfigMode.Default:
            extraData = { ...CPTCustomDefaultConfig, cptConfigMode } as CPTCustomConfiguration;
            break;
          case CPTConfigMode.Demo:
            extraData = { ...CPTCustomDemoConfig, cptConfigMode } as CPTCustomConfiguration;
            break;
          case CPTConfigMode.Adult:
            extraData = { ...CPTAdultCustomDefaultConfig, cptConfigMode } as CPTCustomConfiguration;
            break;
          case CPTConfigMode.AdultDemo:
            extraData = { ...CPTAdultCustomDemoConfig, cptConfigMode } as CPTCustomConfiguration;
            break;
        }
        yield put(
          setTestConfigurationExtraData({
            extraData,
          }),
        );
      }
      break;
    case TestCategory.OcularMotility: {
      const ocularMotilityAdminConfiguration: OcularMotilityAdminConfiguration = yield select(
        selectOcularMotilityAdminConfiguration,
      );
      const currentExtraData = currentConfiguration?.extraData;
      const hasAntiSaccadesShape = !!currentExtraData && "screenDistanceMeters" in currentExtraData;
      const preservedTrialCount = (currentExtraData as AntiSaccadesCustomConfiguration | null)
        ?.trialCount;
      const preservedChildMode = (currentExtraData as OMTestCustomConfiguration | null)?.childMode;
      if (selectedTestType === TestType.AntiSaccades && !hasAntiSaccadesShape) {
        const trialCount =
          preservedTrialCount === 16 || preservedTrialCount === 32
            ? preservedTrialCount
            : omConfigAntiSaccades.trialCount;
        yield put(
          setTestConfigurationExtraData({
            extraData: { ...omConfigAntiSaccades, trialCount },
          }),
        );
      } else if (selectedTestType !== TestType.AntiSaccades) {
        // XIH and pursuit share field names, so the shape cannot say whose values these are.
        const patternExtraData = resolveOcularMotilityExtraData(
          selectedTestType,
          ocularMotilityAdminConfiguration,
          preservedChildMode,
        );
        if (patternExtraData && !isEqual(currentExtraData, patternExtraData)) {
          yield put(setTestConfigurationExtraData({ extraData: patternExtraData }));
        }
      }

      const currentConfigOptions = yield select(selectAvailablePreTestConfigurationOptions);
      const baseConfigOptions = yield select(selectBaseAvailablePreTestConfigurationOptions);
      const omScopeList = yield select(selectScopeList);
      const changedConfigOptions = filterTestConfigurationOptions(
        GetUpdatedAvailableOMConfigsAfterConfigUpdate(
          currentConfiguration as TestConfiguration,
          baseConfigOptions,
        ),
        version,
        omScopeList ?? [],
      );

      if (!isEqual(changedConfigOptions, currentConfigOptions)) {
        yield put(
          setAvailableConfigurations({
            availableOptions: changedConfigOptions,
            productVersion: version,
          }),
        );
      }
      break;
    }
    case TestCategory.ColorVision: {
      const currentConfigOptions = yield select(selectAvailablePreTestConfigurationOptions);
      const baseConfigOptions = yield select(selectBaseAvailablePreTestConfigurationOptions);
      const cvScopeList = yield select(selectScopeList);
      const changedConfigOptions = filterTestConfigurationOptions(
        GetUpdatedAvailableIshiharaConfigsAfterConfigUpdate(
          currentConfiguration as TestConfiguration,
          baseConfigOptions,
        ),
        version,
        cvScopeList ?? [],
      );

      if (!isEqual(changedConfigOptions, currentConfigOptions)) {
        yield put(
          setAvailableConfigurations({
            availableOptions: changedConfigOptions,
            productVersion: version,
          }),
        );
      }

      const currentExtraData = (currentConfiguration?.extraData ??
        {}) as ColorVisionCustomConfiguration;
      const plateCount = currentExtraData.plateCount ?? color_vision_plates_en.length;
      const translatedPlates = translateColorVisionPlates(
        color_vision_plates_en,
        currentConfiguration?.virtualAssistanceConfig?.showAssistant
          ? currentConfiguration.virtualAssistanceConfig.assistantLanguage
          : currentConfiguration?.testLanguage,
      );
      const mergedExtraData: ColorVisionCustomConfiguration = {
        ...currentExtraData,
        questionsJson: translatedPlates,
        plateCount,
        showTutorial:
          currentConfiguration?.testType === TestType.ColorVision
            ? false
            : currentExtraData.showTutorial,
      };
      if (!isEqual(currentExtraData, mergedExtraData)) {
        yield put(setTestConfigurationExtraData({ extraData: mergedExtraData }));
      }

      break;
    }

    default:
      throw new Error(`Invalid Test Name: ${selectedTestName}`);
  }

  if (currentConfiguration?.testType === TestType.Ascending) {
    const currentUIConfiguration = yield select(selectTestUIConfiguration);
    yield put(
      setTestUIConfiguration({
        ...currentUIConfiguration,
        instructions: ascendingInstruction,
      }),
    );
  } else if (currentConfiguration?.testType === TestType.Threshold) {
    const currentUIConfiguration = yield select(selectTestUIConfiguration);
    yield put(
      setTestUIConfiguration({
        ...currentUIConfiguration,
        instructions: thresholdInstruction,
      }),
    );
  }

  if (
    currentConfiguration.virtualAssistanceConfig?.assistantLanguage ||
    currentConfiguration.virtualAssistanceConfig?.showAssistant !== undefined
  ) {
    yield call(fetchAndUpdateVirtualAssistanceVideos);
  }
  yield put(updatePersistedConfigFromTestConfig(currentConfiguration as TestConfiguration));

  yield put(testConfigurationUpdateComplete());
}

function deriveAvailableOptionsForConfiguration(
  selectedTestName: TestCategory | null,
  currentConfiguration: TestConfiguration | null,
  baseConfigOptions: AvailablePreTestConfigurationOptions,
  catchTrialAvailability: MovingFixationCatchTrialAvailability,
) {
  if (!selectedTestName || !currentConfiguration) {
    return baseConfigOptions;
  }

  switch (selectedTestName) {
    case TestCategory.VisualField:
      return GetUpdatedAvailableVFAConfigsAfterConfigUpdate(
        currentConfiguration,
        baseConfigOptions,
        catchTrialAvailability,
      );
    case TestCategory.VisualAcuity:
      return GetUpdatedAvailableVisualAcuityConfigsAfterConfigUpdate(
        currentConfiguration,
        baseConfigOptions,
      );
    case TestCategory.Kinetic:
      return GetUpdatedAvailableKineticConfigsAfterConfigUpdate(
        currentConfiguration,
        baseConfigOptions,
      );
    case TestCategory.ColorVision:
      return GetUpdatedAvailableIshiharaConfigsAfterConfigUpdate(
        currentConfiguration,
        baseConfigOptions,
      );
    default:
      return baseConfigOptions;
  }
}

function* handleSelectedProductCapabilitiesChange(action: any) {
  const payload = action?.payload;
  const isScopeChange =
    action?.type === UPDATE_PRODUCT_SCOPE ||
    (payload && typeof payload === "object" && "scopeList" in payload);
  const isSelectionOrVersionChange =
    action?.type === UPDATE_CURRENT_VERSION ||
    action?.type === RESET_PRODUCT_STATE ||
    (payload &&
      typeof payload === "object" &&
      ("pid" in payload || "currentVersion" in payload || "osVersion" in payload));

  if (!isSelectionOrVersionChange && !isScopeChange) {
    return;
  }

  const testStatus: TestStatus = yield select(selectTestStatus);
  if (
    testStatus === TestStatus.StartRequested ||
    testStatus === TestStatus.VirtualAssistant ||
    testStatus === TestStatus.Started ||
    testStatus === TestStatus.Paused
  ) {
    return;
  }

  const selectedTestName: TestCategory | null = yield select(getSelectedTestName);

  if (isScopeChange && selectedTestName) {
    const scopeList = yield select(selectScopeList);
    const currentVersion = yield select(selectCurrentVersion);
    const { test: resolvedTest, isSelectedInScope } = resolveInScopeTest(
      scopeList,
      currentVersion,
      selectedTestName,
    );
    if (!isSelectedInScope) {
      yield put(selectTest(resolvedTest));
      return;
    }
  }

  const currentConfiguration: TestConfiguration | null = yield select(selectTestConfiguration);
  const baseConfigOptions: AvailablePreTestConfigurationOptions | null = yield select(
    selectBaseAvailablePreTestConfigurationOptions,
  );

  if (!selectedTestName || !currentConfiguration || !baseConfigOptions) {
    return;
  }

  const productVersion = yield select(selectCurrentVersion);
  const catchTrialsSwitchedOn: boolean = yield select(selectMovingFixationCatchTrialsEnabled);
  const availableOptions = deriveAvailableOptionsForConfiguration(
    selectedTestName,
    currentConfiguration,
    baseConfigOptions,
    { catchTrialsSwitchedOn, headsetVersion: productVersion },
  );

  yield put(
    setAvailableConfigurations({
      availableOptions,
      productVersion,
    }),
  );
}

function* handleTestStatusChange(action: PayloadAction<{ status: TestStatus; extraData?: any }>) {
  const extraData = action.payload.extraData;
  const selectedTest = yield select(getSelectedTestName);

  switch (action.payload.status) {
    case TestStatus.DeviceSelection:
    case TestStatus.PatientSelection:
      yield put(updateOnTestProgressPage(false));
      yield put(resetTestSession());
      yield put(resetVfaAutoRestartCount());
      yield put(clearDoNotShowAgainList());
      yield put(setLatestTestInteractionDate(null));
      break;
    case TestStatus.TestConfiguration:
      {
        const wasResumedSession = yield select(selectIsResumedTestSession);
        const currentConfiguration: TestConfiguration | null =
          yield select(selectTestConfiguration);
        const availableConfigurations: AvailablePreTestConfigurationOptions | null = yield select(
          selectAvailablePreTestConfigurationOptions,
        );
        const persistedConfiguration: PersistedConfiguration = yield select(
          selectPersistedConfiguration,
        );
        yield put(toggleDoTheOtherEyeButtonState(false));
        yield put(updateOnTestProgressPage(false));
        yield put(clearTestRelatedData());
        yield put(clearTestProgress());

        let didNormalizeConfiguration = false;

        if (currentConfiguration && availableConfigurations) {
          const configurationForConfigScreen = sanitizeTestConfiguration(
            mergePersistedFields(
              persistedConfiguration,
              currentConfiguration,
              availableConfigurations,
            ),
            availableConfigurations,
          );

          if (!isEqual(currentConfiguration, configurationForConfigScreen)) {
            yield put(updateTestConfiguration(configurationForConfigScreen));
            yield waitForActions(testConfigurationUpdateComplete.type, 500);
            didNormalizeConfiguration = true;
          }
        }

        if (!didNormalizeConfiguration) {
          yield call(fetchAndUpdateVirtualAssistanceVideos);
        }

        const scopeList = yield select(selectScopeList);
        const currentVersion = yield select(selectCurrentVersion);
        const { test: resolvedTest, isSelectedInScope } = resolveInScopeTest(
          scopeList,
          currentVersion,
          selectedTest,
        );

        if (!selectedTest || !isSelectedInScope || wasResumedSession) {
          // Re-select the test so handleTestSelectionChange reloads the default
          // configuration (wiping any config that was loaded from the headset),
          // and so a previously selected test that is no longer in the org's
          // scope is replaced with the first available one.
          yield put(selectTest(resolvedTest));
        }
        const testId = uuidv4();
        yield put(setTestId(testId));
      }
      break;
    case TestStatus.StartRequested:
      {
        const availableConfigurations: AvailablePreTestConfigurationOptions | null = yield select(
          selectAvailablePreTestConfigurationOptions,
        );
        if (hasLoadingMandatoryConfigurationOption(availableConfigurations)) {
          yield put(setTestStatus({ status: TestStatus.TestConfiguration }));
          return;
        }

        // Check and refresh token before starting VR test
        const tokenValid = yield call(SecureTokenManager.refreshIfNeeded);

        if (!tokenValid) {
          console.error("Token validation failed - redirecting to login");
          yield put(logOut("/login"));
          return;
        }

        // Reset completion service when starting new test
        const completionService = TestCompletionService.getInstance();
        completionService.resetCheckedTests();
        yield put(setTestPhase(null));
        yield put(setIsSceneReady(false));
        yield put(sendTestStartMessage({ isRetry: false }));
      }
      break;
    case TestStatus.VirtualAssistant:
    case TestStatus.Started:
      {
        yield put(updateOnTestProgressPage(true));
      }
      break;
    case TestStatus.Paused: {
      //Do nothing
      break;
    }
    case TestStatus.CompleteRequested:
      {
        const testName = yield select(getSelectedTestName);
        const testEventCodes = yield select(selectTestEventCodes);
        const testId = yield select(selectTestId);
        const productId = yield select(selectProductId);
        yield call(sendTestFinishRequest, testEventCodes, productId, testId, extraData);

        if (testName === TestCategory.AmslerGrid) {
          //todo: right now finish requests for tests above do not invoke a finish response from backend so we handle this here. This causes unnecessary code repat and complexity.
          yield put(setTestStatus({ status: TestStatus.Completed }));
        }

        if (testId) {
          yield call(checkBackendCompletion, testId);
        }
      }

      break;
    case TestStatus.WaitingForNextEye: {
      yield put(setTestCompletedPercentage(50));
      yield call(closeRecoveryDialogIfOpen);
      break;
    }
    case TestStatus.Completed: {
      yield put(setTestCompletedPercentage(100));
      yield put(updateOnTestProgressPage(false));
      yield call(closeRecoveryDialogIfOpen);

      const testId = yield select(selectTestId);
      if (testId) {
        yield put(addCompletedTestId(testId));
      }

      break;
    }
    case TestStatus.TerminateRequested:
      {
        yield put(resetPauseState());
        yield put(sendTestTerminateMessage({ isRetry: false }));
      }
      break;
    case TestStatus.Error:
      break;
    default:
      throw new Error(`Invalid Test Status: ${action.payload}`);
  }
}

// The headset lingers in the finished test scene (end animation) after a
// completion, and START_TEST sent into that scene is lost — wait for Menu the
// same way the live path waits for the post-terminate config screen.
function* waitForHeadsetMenuScene() {
  while (true) {
    const scene: string | null = yield select(selectScene);
    if (scene?.toLowerCase() === "menu") return true;
    const action = yield call(waitForActions, UPDATE_SCENE, 30000);
    if (!action) return false;
  }
}

function* waitForMenuBeforeStart() {
  const awaitingMenuSince: number | null = yield select(selectAwaitingMenuSince);
  if (awaitingMenuSince == null) return;
  const remainingMs = awaitingMenuSince + START_MENU_WAIT_TIMEOUT_MS - Date.now();
  if (remainingMs <= 0) return;
  yield race({
    menu: call(waitForHeadsetMenuScene),
    timeout: delay(remainingMs),
  });
}

function* handleVfaReliabilityAutoRestart() {
  const isAutoRestartAllowed: boolean = yield select(selectIsReliabilityAutoRestartAllowed);
  if (!isAutoRestartAllowed) {
    return;
  }

  const currentStatus: TestStatus = yield select(selectTestStatus);
  const isOperatorPaused: boolean = yield select(selectIsOperatorPaused);
  // A breach detected in the last catch trials only reaches us as the run ends,
  // so a completed test is restartable too — it just needs a different handoff
  // than a live one (nothing to terminate, and the headset is on its way to Menu).
  const isCompleted = currentStatus === TestStatus.Completed;
  if (!isCompleted && (!isTestStatusRunning(currentStatus) || isOperatorPaused)) {
    return;
  }

  const restartCount: number = yield select(selectVfaAutoRestartCount);
  if (restartCount >= VFA_RELIABILITY_MAX_AUTO_RESTARTS) {
    return;
  }
  const selectedTest: TestCategory | undefined = yield select(getSelectedTestName);

  const activeTestIndex: number | null = yield select(selectActiveTestIndex);
  const tests: TestSuiteItem[] = yield select(selectTestSuiteTests);
  const isSuiteTest = activeTestIndex !== null && activeTestIndex < tests.length;
  const currentSuiteTest = isSuiteTest ? tests[activeTestIndex] : null;

  if (isCompleted) {
    // Completion already armed the suite autoplay countdown; the next test must
    // not start while we are waiting for the headset to leave the finished scene.
    if (isSuiteTest) {
      yield put(setAutoPlayCountdown(null));
    }
    const reachedMenu: boolean = yield call(waitForHeadsetMenuScene);
    // A headset that never reports Menu restarts nothing, so it must not spend
    // one of the allowed attempts either.
    if (!reachedMenu) return;
    yield put(incrementVfaAutoRestartCount(selectedTest));
    yield put(setTestStatus({ status: TestStatus.TestConfiguration }));
  } else {
    yield put(incrementVfaAutoRestartCount(selectedTest));
    yield put(
      setTestStatus({
        status: TestStatus.TerminateRequested,
        extraData: isSuiteTest ? { isRestart: true } : undefined,
      }),
    );

    while (true) {
      const action = yield call(waitForActions, setTestStatus.type, 30000);
      if (!action) return;
      if (action.payload?.status === TestStatus.TestConfiguration) break;
    }
  }

  yield delay(500);

  if (isSuiteTest && currentSuiteTest) {
    yield put(
      restartTestInSuite({
        testToRemoveId: currentSuiteTest.id,
        testToRestart: {
          id: currentSuiteTest.id,
          configuration: currentSuiteTest.configuration,
          completedTestResultId: currentSuiteTest.completedTestResultId,
        },
        testIndex: activeTestIndex,
        skipNextAutoPlay: true,
      }),
    );
    return;
  }

  yield put(clearCollectedTestData());
  yield put(setTestStatus({ status: TestStatus.StartRequested }));
}

function* handleSendMessageToHeadSet(action: PayloadAction<{ extraData?: any }>) {
  const testEventCodes = yield select(selectTestEventCodes);
  const testId = yield select(selectTestId);
  const productId = yield select(selectProductId);
  const organizationIds = yield select(selectUserOrgId);
  yield call(
    sendGenericNextMessage,
    testEventCodes,
    productId,
    organizationIds,
    testId,
    action.payload,
  );
}

function* handleSetTestProgressConfiguration(
  action: PayloadAction<TestProgressConfiguration | null>,
) {
  if (!action.payload) {
    return;
  }

  const testStatus: TestStatus = yield select(selectTestStatus);
  const shouldSyncProgressConfiguration = [
    TestStatus.VirtualAssistant,
    TestStatus.StartRequested,
    TestStatus.Started,
    TestStatus.Paused,
  ].includes(testStatus);

  // Pre-test configuration changes should not be mirrored back into the live test state
  // or pushed to the headset. Doing that creates unnecessary update churn while the user
  // is still configuring the test.
  if (!shouldSyncProgressConfiguration) {
    return;
  }

  yield put(mergeProgressConfigToMain(action.payload));
  yield waitForActions(mergeProgressConfigToMain.type, 500);

  const testName = yield select(getSelectedTestName);
  let payload;
  if (testName === TestCategory.VisualField) {
    const currentConfiguration = yield select(selectTestConfiguration);
    payload = currentConfiguration;
  } else {
    payload = action.payload;
  }
  yield call(sendProgressConfigurationUpdateMessage, payload, testName);
}

function* handleCompletionPercentageChange() {
  const completionData = yield select(selectTestCompletionCheckData);

  if (completionData.shouldCheck) {
    yield call(checkBackendCompletion, completionData.testId);
  }
}

function* handleResumeTest(action: PayloadAction<{ testName: TestCategory }>) {
  // A resumed test skips the normal selection flow, so availableProgress-
  // Configurations is still null and ProgressConfigView would render no
  // config controls at all. Rebuild the options from the test definition,
  // version-filtered like handleTestSelectionChange does.
  const testDefinition = TEST_DEFINITIONS[action.payload.testName];
  if (testDefinition?.availableProgressConfigurations) {
    const productVersion = yield select(selectCurrentVersion);
    yield put(
      setAvailableProgressConfigurations({
        options: filterTestConfigurationOptions(
          testDefinition.availableProgressConfigurations as AvailablePreTestConfigurationOptions,
          productVersion,
        ),
        productVersion,
      }),
    );
  }
  yield put(updateOnTestProgressPage(true));
}

const SCENE_RECOVERY_COMPLETION_RETRIES = 3;
const SCENE_RECOVERY_COMPLETION_RETRY_DELAY_MS = 1000;

function* checkBackendCompletion(
  testId: string,
  options: { force?: boolean; retries?: number; retryDelayMs?: number } = {},
) {
  const {
    force = false,
    retries = 0,
    retryDelayMs = SCENE_RECOVERY_COMPLETION_RETRY_DELAY_MS,
  } = options;
  try {
    const completionService = TestCompletionService.getInstance();
    const method =
      force || retries > 0 ? "forceCheckBackendTestCompletion" : "checkBackendTestCompletion";

    let isCompleted = false;
    for (let attempt = 0; ; attempt++) {
      isCompleted = yield call([completionService, method], testId);
      if (isCompleted || attempt >= retries) {
        break;
      }
      yield delay(retryDelayMs);
    }

    const isWaitingForNextEyeOnFinish = yield select(selectShouldWaitForNextEyeOnFinish);

    if (isCompleted && !isWaitingForNextEyeOnFinish) {
      yield put(setTestStatus({ status: TestStatus.Completed }));
    }
  } catch (error) {
    console.error("Error checking backend completion:", error);
  }
}

function* closeRecoveryDialogIfOpen() {
  const dialog = yield select((state) => state.dialog);
  if (dialog?.isOpen && dialog?.title === i18n.t("TestRecovery.title")) {
    yield put(closeConfirmation());
  }
}

function* pollBackendCompletionWhileRecovering(testId: string) {
  for (let attempt = 0; attempt < RECOVERY_DIALOG_POLL_MAX_ATTEMPTS; attempt++) {
    yield delay(RECOVERY_DIALOG_POLL_INTERVAL_MS);

    const dialog = yield select((state) => state.dialog);
    const isRecoveryDialogOpen = dialog?.isOpen && dialog?.title === i18n.t("TestRecovery.title");
    const status: TestStatus = yield select(selectTestStatus);
    const currentTestId: string | null = yield select(selectTestId);

    if (
      !isRecoveryDialogOpen ||
      currentTestId !== testId ||
      (status !== TestStatus.Started && status !== TestStatus.VirtualAssistant)
    ) {
      return;
    }

    yield call(checkBackendCompletion, testId, { force: true });

    const statusAfter: TestStatus = yield select(selectTestStatus);
    if (statusAfter === TestStatus.Completed) {
      return;
    }
  }
}

function* handleProductSceneChange(
  action: PayloadAction<{ scene: string; lastInteraction: Date }>,
) {
  const scene = action.payload.scene;
  const testState = yield select((state) => state.test);
  const sceneLower = scene?.toLowerCase();
  const wasActive =
    testState.status === TestStatus.Started || testState.status === TestStatus.VirtualAssistant;

  // Test/assistant scenes also reach us via REST scene polling (ETag-gated,
  // so only real changes land here) — a fallback for lost SCENE_SET_TEST
  // pushes on slow networks: without it the web stays on the start screen or
  // never opens the assistant stream window while the headset plays.
  if (sceneLower === "test" || sceneLower === "virtualassistant") {
    const targetStatus = sceneLower === "test" ? TestStatus.Started : TestStatus.VirtualAssistant;
    // After a finish the headset lingers in the test scene (end animation) and
    // late scene reports from the finished test would yank a config screen back
    // into a progress view — distrust them until the headset is seen in Menu.
    const awaitingMenu: boolean = yield select(selectIsAwaitingMenuSceneAfterCompletion);
    const canFlip =
      wasActive ||
      ((testState.status === TestStatus.TestConfiguration ||
        testState.status === TestStatus.StartRequested) &&
        !awaitingMenu);
    if (canFlip && testState.status !== targetStatus) {
      yield put(setTestStatus({ status: targetStatus }));
    }
    // The headset is demonstrably back in the flow: drop the recovery dialog
    // opened while it looked gone. Only ours — scene polling can re-dispatch
    // an unchanged scene, and that must not dismiss unrelated confirmations
    // (e.g. a skip-test prompt) mid-test.
    if (wasActive) {
      yield call(closeRecoveryDialogIfOpen);
    }
  }

  if (sceneLower === "menu") {
    if (!wasActive) {
      return;
    }
    // Fielded 2.8.6 headsets report Menu once while the Test scene loads. The
    // backend still tracks the run as started then, so the report is stale.
    const isRunStillOnHeadset: boolean = yield call(isCurrentTestRunningOnHeadset);
    if (isRunStillOnHeadset) {
      yield call(showHeadsetScene, "Test");
      return;
    }
    const completionData = yield select(selectTestCompletionCheckData);
    if (completionData.shouldCheck) {
      yield call(checkBackendCompletion, completionData.testId, {
        force: true,
        retries: SCENE_RECOVERY_COMPLETION_RETRIES,
      });
    }

    // After the completion check, re-read status — it may have transitioned.
    const statusAfterCheck: TestStatus = yield select(selectTestStatus);
    if (statusAfterCheck !== TestStatus.Started && statusAfterCheck !== TestStatus.VirtualAssistant)
      return;

    // The headset dropped back to menu while the web still shows an active
    // test. Show a recovery dialog.
    const suiteTests: TestSuiteItem[] = yield select(selectTestSuiteTests);
    const isTestSuite = suiteTests.length > 0;
    const t = i18n.t.bind(i18n);

    if (isTestSuite) {
      // The dialog can stay open while suite state moves (repeated scene
      // events, parallel sagas), so the callbacks re-read the live state at
      // click time instead of closing over snapshots.
      yield put(
        openConfirmation(
          t("TestRecovery.title"),
          t("TestRecovery.messageSuite"),
          () => {
            // Full suite restart flow: resets the item, rebuilds the config
            // and mints a fresh test uuid. Re-sending the old uuid would be
            // absorbed by the backend's duplicate-start suppression.
            const state = store.getState();
            const activeTest = selectActiveTest(state);
            const activeIndex = selectActiveTestIndex(state);
            if (activeTest && activeIndex != null) {
              store.dispatch(
                restartTestInSuite({
                  testToRemoveId: activeTest.id,
                  testToRestart: {
                    id: activeTest.id,
                    configuration: activeTest.configuration,
                    completedTestResultId: activeTest.completedTestResultId,
                  },
                  testIndex: activeIndex,
                }),
              );
            } else {
              store.dispatch(setTestId(uuidv4()));
              store.dispatch(setTestStatus({ status: TestStatus.StartRequested }));
            }
          },
          false,
          t("TestRecovery.moveToNext"),
          t("TestRecovery.restart"),
          () => {
            const state = store.getState();
            const activeTest = selectActiveTest(state);
            const activeIndex = selectActiveTestIndex(state);
            if (activeTest && activeIndex != null) {
              store.dispatch(updateTestStatus({ id: activeTest.id, status: "skipped" }));
            }
            // startNextTest advances the active index itself; advance manually
            // only when waiting for the user to start the next test.
            if (selectAutoPlay(state)) {
              store.dispatch(startNextTest());
            } else {
              store.dispatch(moveToNextTest());
            }
          },
        ),
      );
    } else {
      yield put(
        openConfirmation(
          t("TestRecovery.title"),
          t("TestRecovery.messageSingle"),
          () => {
            // A restart is a new run: a fresh uuid bypasses the backend's
            // duplicate-start suppression for the interrupted run.
            store.dispatch(setTestId(uuidv4()));
            store.dispatch(setTestStatus({ status: TestStatus.StartRequested }));
          },
          false,
          t("eventlessDialog.goBack"),
          t("TestRecovery.restart"),
          () => {
            store.dispatch(setTestStatus({ status: TestStatus.TestConfiguration }));
          },
        ),
      );
    }

    // Blocking call (not fork): handleProductSceneChange runs under takeLatest,
    // so the next scene update cancels this poll cleanly and at most one poll
    // loop is ever active. This is the last step, so nothing waits on it.
    yield call(pollBackendCompletionWhileRecovering, completionData.testId);
  }
}

/**
 * @summary
 * When a video is set as current video, and if it is marked as "shouldAppearOnce",
 * we add it to the doNotShowAgainList to prevent future appearances.
 * (Until the user goes to device selection)
 */
function* handleSetCurrentVirtualAssistanceVideo(action: PayloadAction<string | null>) {
  if (action.payload != null) {
    const virtualAssistanceConfiguration: VirtualAssistantConfig | null = yield select(
      selectVirtualAssistantConfig,
    );
    const video: AssistanceVideo | undefined = virtualAssistanceConfiguration?.videoInfos?.find(
      (vid) => vid.videoName === action.payload,
    );
    if (video && video.shouldAppearOnce) {
      yield put(addVideosToDoNotShowAgainList([video.videoName]));
    }
  }
}

function* handleTestStartMessage(action: PayloadAction<{ isRetry: boolean }>) {
  const startWasRefusedByLicence: boolean = yield call(enforceLicenceReadOnlyPolicy);
  if (startWasRefusedByLicence) {
    return;
  }

  const startWasIntercepted: boolean = yield call(enforceOfflineHeadsetUpdatePolicy, action);
  if (startWasIntercepted) {
    return;
  }

  yield call(waitForMenuBeforeStart);

  const statusAfterWait: TestStatus = yield select(selectTestStatus);
  if (statusAfterWait !== TestStatus.StartRequested) {
    return;
  }

  const isRetry = action.payload.isRetry;
  let testEventCodes = yield select(selectTestEventCodes);
  let testId = yield select(selectTestId);
  const productId = yield select(selectProductId);
  const productVersion: HeadsetVersion = yield select(selectCurrentVersion);
  const organizationIds = yield select(selectUserOrgId);

  const testConfiguration = yield select(selectTestConfiguration);
  const patient = yield select(selectSelectedPatient);
  const activeTestIndex: number | null = yield select(selectActiveTestIndex);
  const suiteTests: TestSuiteItem[] = yield select(selectTestSuiteTests);
  const isSuiteRunning: boolean = yield select(selectIsSuiteRunning);
  const finishedSuiteTestCount = suiteTests.filter((test) =>
    ["completed", "terminated", "skipped", "error"].includes(test.status),
  ).length;
  const suiteSessionInfo =
    activeTestIndex !== null && suiteTests.length > 0
      ? {
          testIndex: isSuiteRunning ? activeTestIndex : finishedSuiteTestCount,
          totalTests: suiteTests.length,
        }
      : undefined;

  // Some restart flows clear transient test state before requesting a fresh start.
  // Ensure the outbound start message always carries a valid UUID.
  if (!testId) {
    testId = uuidv4();
    yield put(setTestId(testId));
  }

  // Retries too: another page may have started a test since this one was sent.
  const runState: DeviceRunState | null = yield call(getDeviceRunState, productId);
  const isStillRequestingThisStart: boolean = yield call(isStillRequestingStart, testId, productId);
  if (!isStillRequestingThisStart) {
    return;
  }
  const runningTestId = runningTestIdOnHeadset(runState);
  const decision = decideStartAgainstRunningTest(runningTestId, testId);
  if (decision === "attach") {
    yield put(attachToRunningTest({ testId: runningTestId, scene: runState.scene }));
    return;
  }
  if (decision === "refuse") {
    yield call(showRunningTestPage, runState.scene);
    return;
  }

  // If testEventCodes is null, try to select the test to set event codes
  // This can happen when starting a test suite if the test wasn't properly selected first
  // This happened when we were testing suite with Emre
  if (!testEventCodes && testConfiguration?.name) {
    console.warn(
      "testEventCodes is null, selecting test to set event codes:",
      testConfiguration.name,
    );
    yield put(selectTest(testConfiguration.name));
    // Wait a bit for the saga to process the selectTest action
    yield call(() => new Promise((resolve) => setTimeout(resolve, 100)));
    testEventCodes = yield select(selectTestEventCodes);
  }

  const expectedEventCodes = testConfiguration?.name
    ? TEST_DEFINITIONS[testConfiguration.name]?.eventCodes
    : null;
  if (expectedEventCodes && !isEqual(expectedEventCodes, testEventCodes)) {
    console.warn(
      "testEventCodes out of sync with the selected test, re-deriving for:",
      testConfiguration.name,
    );
    yield put(setTestEventCodes(expectedEventCodes));
    testEventCodes = expectedEventCodes;
  }

  const configToSend: TestConfiguration = cloneDeep(testConfiguration) as TestConfiguration;
  if (
    configToSend.name === TestCategory.ContinuousPerformance &&
    configToSend.testType === TestType.IdenticalPairs &&
    configToSend.virtualAssistanceConfig
  ) {
    const doNotShowAgainList: string[] = yield select(selectVirtualAssistantDoNotShowAgainList);
    configToSend.virtualAssistanceConfig.videoInfos = getVirtualAssistantVideoListForTest(
      configToSend,
      doNotShowAgainList,
      productVersion,
    );
  }
  if (
    configToSend.name === TestCategory.ContinuousPerformance &&
    (configToSend.extraData as CPTCustomConfiguration)?.cptConfigMode === CPTConfigMode.AdultDemo
  ) {
    (configToSend.extraData as CPTCustomConfiguration).cptConfigMode = CPTConfigMode.Adult;
  }

  // The event-code wait above can outlast the operator's attempt too.
  const isStillRequestingAtSend: boolean = yield call(isStillRequestingStart, testId, productId);
  if (!isStillRequestingAtSend) {
    return;
  }
  if (isRetry) {
    console.warn("Retrying test start request for testId:", testId);
  }
  yield call(
    sendTestStartRequest,
    testEventCodes,
    productId,
    organizationIds,
    testId,
    configToSend,
    patient,
    suiteSessionInfo,
  );
  recordTestStartOnThisPage(testId);

  yield put(clearAwaitingMenuScene());
}

// Not takeLatest: on a slow backend each retry would restart the running-test
// check, and no start would ever go out.
function* watchTestStartMessages() {
  let startTask: Task | null = null;
  while (true) {
    const action: PayloadAction<{ isRetry: boolean }> = yield take(sendTestStartMessage.type);
    if (startTask?.isRunning()) {
      if (action.payload.isRetry) {
        continue;
      }
      yield cancel(startTask);
    }
    startTask = yield fork(handleTestStartMessage, action);
  }
}

function* isCurrentTestRunningOnHeadset(): Generator<any, boolean, any> {
  const productId: string | null = yield select(selectProductId);
  const currentTestId: string | null = yield select(selectTestId);
  if (!productId || !currentTestId) {
    return false;
  }
  const runState: DeviceRunState | null = yield call(getDeviceRunState, productId);
  return runningTestIdOnHeadset(runState) === currentTestId;
}

function* handleAttachToRunningTest(
  action: PayloadAction<{ testId: string; scene: string | null }>,
) {
  const { testId, scene } = action.payload;
  yield put(setTestId(testId));
  yield call(reconnectAndRequestCurrentTestDetails);
  yield call(showHeadsetScene, sceneOfRunningTest(scene));
  yield put(setTestStatus({ status: testStatusForRunningScene(scene) }));
}

function* showHeadsetScene(scene: string) {
  yield put(
    updateDeviceStatus(isCalibrationScene(scene) ? DEVICE_STATUS.Calibration : DEVICE_STATUS.Test),
  );
  // Last: it restarts the UPDATE_SCENE saga, cancelling a caller running under it.
  yield put(updateScene(scene, new Date()));
}

function* isStillRequestingStart(testId: string, productId: string): Generator<any, boolean, any> {
  const status: TestStatus = yield select(selectTestStatus);
  const currentTestId: string | null = yield select(selectTestId);
  const currentProductId: string | null = yield select(selectProductId);
  return (
    status === TestStatus.StartRequested &&
    currentTestId === testId &&
    currentProductId === productId
  );
}

function* showRunningTestPage(runningScene: string | null) {
  yield call(resetActiveSuiteTestToNotStarted);
  // Before the scene: while starting, a test scene report adopts the other test.
  yield put(setTestStatus({ status: TestStatus.PatientSelection }));
  yield call(showHeadsetScene, sceneOfRunningTest(runningScene));
  yield call(navigate, "/new-test");
}

function* handleTestTerminateMessage(action: PayloadAction<{ isRetry: boolean }>) {
  const isRetry = action.payload.isRetry;
  const testId = yield select(selectTestId);
  const productId = yield select(selectProductId);

  if (isRetry) {
    console.warn("Retrying test terminate request for testId:", testId);
  }

  yield call(sendTestTerminateRequest, productId, testId);

  const scene: string | null = yield select(selectScene);
  if (scene?.toLowerCase().includes("calibration")) {
    yield call(sendSkipCalibration);
  }
}

function* handleSetTestConfigurationExtraData(
  _action: PayloadAction<{ extraData: TestSpecificConfig }>,
) {
  const currentTestConfiguration: TestConfiguration = yield select(selectTestConfiguration);
  yield call(fetchAndUpdateVirtualAssistanceVideos);
  yield put(setTestProgressConfiguration(currentTestConfiguration));
}

function* handleSetAvailableConfigurations(
  action: PayloadAction<{
    availableOptions: AvailablePreTestConfigurationOptions;
    productVersion: HeadsetVersion | null;
    isNewTestSelection?: boolean;
  }>,
) {
  const productVersion = action.payload.productVersion;
  const currentTestConfiguration: TestConfiguration = yield select(selectTestConfiguration);
  const currentAvailableOptions: AvailablePreTestConfigurationOptions = yield select(
    selectAvailablePreTestConfigurationOptions,
  );
  const persistedConfiguration: PersistedConfiguration = yield select(selectPersistedConfiguration);

  const isANewTestSelection =
    action.payload.isNewTestSelection ??
    (currentTestConfiguration &&
      Object.keys(currentTestConfiguration).length === 1 &&
      typeof currentTestConfiguration.name === "string");
  const testName: TestCategory = yield select(getSelectedTestName);
  const testDefinition = TEST_DEFINITIONS[testName];

  const rawOptions = isANewTestSelection
    ? testDefinition.availableOptions
    : action.payload.availableOptions;
  const resolvedOptions = yield call(resolveReadingAssessmentAvailableOptions, rawOptions);
  const scopeList = yield select(selectScopeList);
  const versionFilteredAvailableOptions = filterTestConfigurationOptions(
    resolvedOptions,
    productVersion,
    scopeList ?? [],
  );

  if (isANewTestSelection) {
    yield put(setBaseAvailableConfigurations(resolvedOptions));
  }

  const defaultConfigurationsWithPersistentDataMerged =
    isANewTestSelection && persistedConfiguration && Object.keys(persistedConfiguration).length > 0
      ? mergePersistedFields(
          persistedConfiguration,
          testDefinition.defaultConfiguration,
          versionFilteredAvailableOptions,
        )
      : testDefinition.defaultConfiguration;

  const selectedPatient = yield select(selectSelectedPatient);
  const configurationWithFirstLanguageApplied = isANewTestSelection
    ? applyFirstLanguageToVirtualAssistant(
        defaultConfigurationsWithPersistentDataMerged,
        selectedPatient?.l1,
        versionFilteredAvailableOptions,
      )
    : defaultConfigurationsWithPersistentDataMerged;

  const rawTestConfiguration = isANewTestSelection
    ? configurationWithFirstLanguageApplied
    : currentTestConfiguration;

  const sanitizedTestConfiguration = sanitizeTestConfiguration(
    rawTestConfiguration,
    versionFilteredAvailableOptions,
  );

  const currentAvailableProgressOptions = yield select(selectAvailableProgressConfigurationOptions);
  const versionFilteredAvailableProgressOptions = filterTestConfigurationOptions(
    testDefinition.availableProgressConfigurations as AvailablePreTestConfigurationOptions | null,
    productVersion,
    scopeList ?? [],
  );

  const currentProgressConfiguration = yield select(selectTestProgressConfiguration);

  const rawTestProgressConfiguration = isANewTestSelection
    ? testDefinition.defaultProgressConfiguration
    : (currentProgressConfiguration ?? currentTestConfiguration);
  const sanitizedTestProgressConfiguration = sanitizeTestConfiguration(
    rawTestProgressConfiguration,
    versionFilteredAvailableProgressOptions,
  );

  if (!isEqual(currentAvailableOptions, versionFilteredAvailableOptions)) {
    yield put(
      applyAvailableConfigurations({
        availableOptions: versionFilteredAvailableOptions,
        productVersion,
      }),
    );
  }

  if (!isEqual(currentTestConfiguration, sanitizedTestConfiguration)) {
    yield put(updateTestConfiguration(sanitizedTestConfiguration));
  }

  if (!isEqual(currentAvailableProgressOptions, versionFilteredAvailableProgressOptions)) {
    yield put(
      setAvailableProgressConfigurations({
        options: versionFilteredAvailableProgressOptions,
        productVersion,
      }),
    );
  }

  if (!isEqual(currentProgressConfiguration, sanitizedTestProgressConfiguration)) {
    yield put(setTestProgressConfiguration(sanitizedTestProgressConfiguration));
  }
}

function* handleSaveTestConfiguration() {
  const testConfiguration: TestConfiguration = yield select(selectTestConfiguration);
  const currentTestName: TestCategory = yield select(getSelectedTestName);
  const savedTestConfig: Partial<Record<TestCategory, Partial<TestConfiguration>>> = yield select(
    selectSavedTestConfiguration,
  );

  const configValuesToSave = extractSavedFields(testConfiguration, currentTestName);

  const newSavedConfig: Partial<Record<TestCategory, Partial<TestConfiguration>>> = {
    ...savedTestConfig,
    [currentTestName]: {
      ...savedTestConfig?.[currentTestName],
      ...configValuesToSave,
    },
  };
  yield put(updateSavedTestConfiguration(newSavedConfig));
}

function* handleLoadSavedTestConfiguration() {
  const selectedTestName: TestCategory = yield select(getSelectedTestName);
  const savedTestConfig = yield select(selectSavedTestConfiguration);
  const currentConfiguration: TestConfiguration | null = yield select(selectTestConfiguration);
  const configToLoad: Partial<TestConfiguration> = savedTestConfig?.[selectedTestName] || {};
  const mergedConfig = merge({}, currentConfiguration, configToLoad);
  yield put(updateTestConfiguration(mergedConfig));
}

function* handleResetSavedTestConfiguration() {
  const selectedTestName: TestCategory = yield select(getSelectedTestName);
  yield put(selectTest(selectedTestName));
}

function* handleAddTestSuiteItem(_action: PayloadAction<{ testCategory: TestConfiguration }>) {
  const selectedTestSuiteItem: TestSuiteItem = yield select(selectSelectedTest);
  const testSuiteItemId = selectedTestSuiteItem?.id || null;
  yield put(setTestId(testSuiteItemId));
}

function* handleNextEyeTest(_action: PayloadAction<EyeType>) {
  yield put(setTestStatus({ status: TestStatus.WaitingForNextEye }));
  const currentConfiguration: TestConfiguration = yield select(selectTestConfiguration);

  if (!currentConfiguration) {
    console.error("Cannot start the next test without a current configuration");
    return;
  }

  const testId = yield select(selectTestId);
  if (testId) {
    yield put(addCompletedTestId(testId));
  }

  yield put(clearTestProgress());
  yield put(clearTestRelatedData());

  const newTestId = testId + "2";
  yield put(setTestId(newTestId));
  yield put(setTestCompletedPercentage(50));
  yield put(setIsLastBackToBackEyeTest(true));
}

function* handleManualCheckTestCompletion() {
  const testId: string | null = yield select(selectTestId);
  if (!testId) return;

  yield put(setManualCompletionCheckFailed(false));
  yield put(setManualCompletionCheckSucceeded(false));
  yield put(setManualCompletionCheckInProgress(true));
  try {
    const completionService = TestCompletionService.getInstance();
    const isWaitingForNextEye: boolean = yield select(selectShouldWaitForNextEyeOnFinish);

    if (isWaitingForNextEye) {
      const secondTestId = testId + "2";
      const secondCompleted: boolean = yield call(
        [completionService, "forceCheckBackendTestCompletion"],
        secondTestId,
      );

      if (secondCompleted) {
        yield put(setManualCompletionCheckSucceeded(true));
        yield delay(600);
        yield put(setManualCompletionCheckSucceeded(false));
        yield put(addCompletedTestId(testId));
        yield put(setTestId(secondTestId));
        yield put(setIsLastBackToBackEyeTest(true));
        yield put(setTestStatus({ status: TestStatus.Completed }));
        return;
      }

      const firstCompleted: boolean = yield call(
        [completionService, "forceCheckBackendTestCompletion"],
        testId,
      );

      if (firstCompleted) {
        yield put(setManualCompletionCheckSucceeded(true));
        yield delay(600);
        yield put(setManualCompletionCheckSucceeded(false));
        yield put(startTestingNextEyeInBackToBackTest(EyeType.LeftEye));
        return;
      }
    } else {
      const isCompleted: boolean = yield call(
        [completionService, "forceCheckBackendTestCompletion"],
        testId,
      );

      if (isCompleted) {
        yield put(setManualCompletionCheckSucceeded(true));
        yield delay(600);
        yield put(setManualCompletionCheckSucceeded(false));
        yield put(setTestStatus({ status: TestStatus.Completed }));
        return;
      }
    }

    yield put(setManualCompletionCheckFailed(true));
    yield delay(1500);
    yield put(setManualCompletionCheckFailed(false));
  } catch {
    yield put(setManualCompletionCheckFailed(true));
    yield delay(1500);
    yield put(setManualCompletionCheckFailed(false));
  } finally {
    yield put(setManualCompletionCheckInProgress(false));
    yield put(setManualCompletionCheckFailed(false));
    yield put(setManualCompletionCheckSucceeded(false));
  }
}

export const CALIBRATION_INACTIVITY_TIMEOUT_MS = 60_000;

// The sceneOpened lifecycle event starts the instruction video, and no
// calibration progress is expected until it finishes, so that phase gets its
// own window — long enough to outlast any instruction video, short enough that
// a video which never hands back still releases the UI.
export const CALIBRATION_SCENE_OPENED_TIMEOUT_MS = 300_000;

function* runCalibrationInactivityTimer(timeoutMs: number) {
  yield delay(timeoutMs);

  const isDeviceCalibrationActive: boolean = yield select(selectIsDeviceCalibrationActive);
  const isCurrentlyCalibrating: boolean = yield select(selectIsCurrentlyCalibrating);
  if (!isDeviceCalibrationActive && !isCurrentlyCalibrating) {
    return;
  }

  console.warn(
    `No calibration lifecycle update from the headset for ${timeoutMs}ms; marking calibration unresponsive.`,
  );
  yield put(setCalibrationUnresponsive(true));
}

// Only calibration progress re-arms the window. The headset's STATE heartbeat
// repeats the current scene every ~15s and keeps flowing while calibration is
// wedged, so an unchanged scene must leave the window (and the flag) alone.
// setIsCurrentlyCalibrating also re-arms it: that flag is driven by a separate
// WS calibrationStatus field (not the calibrationLifecycle/scene machinery
// above), so without this it would never gain a release path if the headset
// goes silent mid-calibration.
export function* watchCalibrationInactivity() {
  let timer: Task | null = null;
  let armedScene: string | null = null;

  while (true) {
    const action: { type: string } = yield take([
      setCalibrationLifecycle.type,
      setIsCurrentlyCalibrating.type,
      UPDATE_SCENE,
      REHYDRATE,
    ]);
    const scene: string | null = yield select(selectScene);

    if (action.type === UPDATE_SCENE) {
      if (scene === armedScene) {
        continue;
      }
      yield put(setCalibrationUnresponsive(false));
    }
    armedScene = scene;

    if (timer) {
      yield cancel(timer);
      timer = null;
    }

    const isDeviceCalibrationActive: boolean = yield select(selectIsDeviceCalibrationActive);
    const isCurrentlyCalibrating: boolean = yield select(selectIsCurrentlyCalibrating);
    if (!isDeviceCalibrationActive && !isCurrentlyCalibrating) {
      continue;
    }

    const lifecycle: CalibrationLifecycleStatus | null = yield select(selectCalibrationLifecycle);

    timer = yield fork(
      runCalibrationInactivityTimer,
      lifecycle?.status === "sceneOpened"
        ? CALIBRATION_SCENE_OPENED_TIMEOUT_MS
        : CALIBRATION_INACTIVITY_TIMEOUT_MS,
    );
  }
}

export default function* testSagas() {
  yield fork(watchCalibrationInactivity);
  yield takeLatest(selectTest.type, handleTestSelectionChange);
  yield takeLatest(updateTestConfiguration.type, handleTestConfigurationUpdate);
  yield takeLatest(setTestStatus.type, handleTestStatusChange);
  yield takeLatest(sendMessageToHeadSet.type, handleSendMessageToHeadSet);
  yield takeLatest(setTestProgressConfiguration.type, handleSetTestProgressConfiguration);
  yield takeLatest(setTestCompletedPercentage.type, handleCompletionPercentageChange);
  yield takeLatest(resumeTest.type, handleResumeTest);
  yield takeLatest(UPDATE_SCENE, handleProductSceneChange);
  yield takeLatest(
    [BATCH_UPDATE, RESET_PRODUCT_STATE, UPDATE_CURRENT_VERSION, UPDATE_PRODUCT_SCOPE],
    handleSelectedProductCapabilitiesChange,
  );
  yield takeLatest(setCurrentVirtualAssistanceVideo.type, handleSetCurrentVirtualAssistanceVideo);
  yield fork(watchTestStartMessages);
  yield takeLatest(attachToRunningTest.type, handleAttachToRunningTest);
  yield takeLatest(sendTestTerminateMessage.type, handleTestTerminateMessage);
  yield takeLatest(setTestConfigurationExtraData.type, handleSetTestConfigurationExtraData);
  yield takeLatest(setAvailableConfigurations.type, handleSetAvailableConfigurations);
  yield takeLatest(saveTestConfiguration.type, handleSaveTestConfiguration);
  yield takeLatest(loadSavedTestConfiguration.type, handleLoadSavedTestConfiguration);
  yield takeLatest(resetSavedTestConfiguration.type, handleResetSavedTestConfiguration);
  yield takeLatest(
    [reorderTests.type, loadTestSuite.type, setSelectedTest.type],
    handleAddTestSuiteItem,
  );
  yield takeLatest(startTestingNextEyeInBackToBackTest.type, handleNextEyeTest);
  yield takeLatest(manualCheckTestCompletion.type, handleManualCheckTestCompletion);
  yield takeLatest(triggerVfaReliabilityAutoRestart.type, handleVfaReliabilityAutoRestart);
}
